import * as XLSX from 'xlsx';
import * as XLSXStyle from 'xlsx-js-style';
import { getAuditFieldDisplayName } from './auditFieldDisplayNames';
import { formatOffsetForDisplay, parseOffsetToMinutes } from './dateFormat';

/**
 * Export a simple table to Excel (one header row + data rows).
 * Same format as AuditDtl Report table: no additional header block (no Form Name, Exported by, etc.),
 * matching Action Item Report — one header row + data only.
 * @param {Array<Object>} dataRows - Array of row objects
 * @param {Array<{ key: string, label: string }>} columnConfig - Columns in order, e.g. [{ key: 'reportID', label: 'Report ID' }]
 * @param {string} fileName - Download filename (e.g. 'VisitExecutionReports_2026-02-15.xlsx')
 */
export function exportToExcel(dataRows, columnConfig, fileName) {
  const headers = columnConfig.map((c) => c.label);
  const keys = columnConfig.map((c) => c.key);
  const rows = [headers];
  for (const row of dataRows) {
    rows.push(keys.map((k) => (row[k] != null && row[k] !== '' ? row[k] : '')));
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  applyAutoFilter(ws, 1, headers.length, rows.length);
  applyReadOnlyProtection(ws, { allowResize: true });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, fileName);
}

/**
 * Export a report to Excel in the same table format as AuditDtl Report but with no additional headers
 * (no Form Name, Exported by, Exported on UTC, Exported At Offset). Same as Action Item Report:
 * one header row + data rows only. Uses styled header row for consistency with Audit report table.
 * @param {Array<Object>} dataRows - Array of row objects
 * @param {Array<{ key: string, label: string }>} columnConfig - Columns in order
 * @param {string} fileName - Download filename
 * @param {Object} options - { sheetName?: string, colWidths?: number[], readOnlyAllowResize?: boolean }
 *   readOnlyAllowResize: if false, keeps the sheet protected but disables resize permissions.
 */
export function exportReportToExcelLikeAuditDtl(dataRows, columnConfig, fileName, options = {}) {
  const sheetName = options.sheetName != null ? options.sheetName : 'Report';
  const headers = columnConfig.map((c) => c.label);
  const keys = columnConfig.map((c) => c.key);
  const rows = [headers];
  for (const row of dataRows) {
    rows.push(keys.map((k) => (row[k] != null && row[k] !== '' ? row[k] : '')));
  }
  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const wb = XLSXStyle.utils.book_new();

  const colCount = headers.length;
  const headerRowStyle = { font: { bold: true } };
  for (let c = 0; c < colCount; c++) {
    const colLetter = XLSXStyle.utils.encode_col(c);
    const ref = `${colLetter}1`;
    if (ws[ref]) ws[ref].s = headerRowStyle;
  }

  if (options.colWidths && Array.isArray(options.colWidths)) {
    ws['!cols'] = options.colWidths.map((wch) => ({ wch: Math.min(wch, 50) }));
  }

  applyAutoFilter(ws, 1, colCount, rows.length);
  applyReadOnlyProtection(ws, { allowResize: options.readOnlyAllowResize !== false });

  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  XLSXStyle.writeFile(wb, fileName, { showGridlines: false });
}

/**
 * Export Action Item Report to Excel with the same header block and structure as Audit Report
 * so the formula/detail bar shows the selected cell's value (see exportAuditReportToExcel).
 * Form Name, blank row, Exported by, Exported on UTC, Exported At Offset, blank row, then column headers and data.
 * Sheet is protected (read-only) with only column/row resizing allowed when readOnlyAllowResize is not false.
 * @param {Array<Object>} dataRows - Array of row objects (keys match columnConfig)
 * @param {Array<{ key: string, label: string }>} columnConfig - Columns in order
 * @param {string} fileName - Download filename
 * @param {Object} options - { formName?, exportedBy?, exportedOnUtc?, offset?, readOnlyAllowResize?: boolean }
 */
export function exportActionItemReportToExcel(dataRows, columnConfig, fileName, options = {}) {
  const formName = options.formName != null ? options.formName : 'Issue Report';
  const exportedBy = options.exportedBy != null ? options.exportedBy : '';
  const exportedOnUtc = options.exportedOnUtc != null ? options.exportedOnUtc : formatUtcDate(new Date());
  const offset = options.offset != null && options.offset !== '' ? options.offset : '+05:30';
  const offsetDisplay = (v) => (formatOffsetForDisplay(v) === '—' ? '' : formatOffsetForDisplay(v));

  const headers = columnConfig.map((c) => c.label);
  const keys = columnConfig.map((c) => c.key);
  const colCount = headers.length;
  const padRow = (arr) => (arr.length >= colCount ? arr : [...arr, ...Array(colCount - arr.length).fill('')]);

  // Header block then blank row, then column headers and data. Pad every row to colCount so each column exists.
  const rows = [
    [`Form Name:- ${formName}`],
    [],
    [`Exported by: ${exportedBy}`],
    [`Exported on UTC: ${exportedOnUtc}`],
    [`Exported At Offset: ${offsetDisplay(offset)}`],
    [],
    headers,
  ];
  rows[0] = padRow(rows[0]);
  rows[1] = padRow(rows[1] || []);
  rows[2] = padRow(rows[2]);
  rows[3] = padRow(rows[3]);
  rows[4] = padRow(rows[4]);
  rows[5] = padRow(rows[5] || []);

  const cell = (v) => (v != null && v !== '' ? String(v) : '');
  dataRows.forEach((row) => {
    rows.push(keys.map((k) => cell(row[k])));
  });

  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const wb = XLSXStyle.utils.book_new();

  const lockedStyle = { protection: { locked: true } };
  if (ws['A1']) ws['A1'].s = { ...formNameStyle, ...lockedStyle };
  if (ws['A3']) ws['A3'].s = { ...headerMetaStyle, ...lockedStyle };
  if (ws['A4']) ws['A4'].s = { ...headerMetaStyle, ...lockedStyle };
  if (ws['A5']) ws['A5'].s = { ...headerMetaStyle, ...lockedStyle };

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: colCount - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: colCount - 1 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: colCount - 1 } },
  ];

  // Ensure used range includes all data so each cell is addressable
  const lastRow = rows.length;
  const lastColLetter = XLSXStyle.utils.encode_col(colCount - 1);
  ws['!ref'] = `A1:${lastColLetter}${lastRow}`;

  // Column header row at Excel row 7 – one cell per column, bold, locked (clickable but not editable)
  for (let c = 0; c < colCount; c++) {
    const colLetter = XLSXStyle.utils.encode_col(c);
    const ref = `${colLetter}7`;
    if (ws[ref]) {
      ws[ref].s = { ...headerRowStyle, ...lockedStyle };
    }
  }

  // Lock all data cells so sheet is clickable but not editable
  for (let r = 8; r <= lastRow; r++) {
    for (let c = 0; c < colCount; c++) {
      const colLetter = XLSXStyle.utils.encode_col(c);
      const ref = `${colLetter}${r}`;
      if (ws[ref]) {
        ws[ref].s = ws[ref].s ? { ...ws[ref].s, ...lockedStyle } : lockedStyle;
      }
    }
  }

  // Column widths for readable layout (Action Item Report when colWidths not passed; callers usually pass colWidths)
  const defaultColWidths = [22, 12, 14, 18, 10, 18, 18, 22, 22, 14, 18];
  const colWidths = (options.colWidths && Array.isArray(options.colWidths))
    ? options.colWidths
    : defaultColWidths;
  ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
    wch: Math.min(Math.max(colWidths[i] || 10, 8), 50),
  }));

  applyAutoFilter(ws, 7, colCount, rows.length);
  applyReadOnlyProtection(ws, { allowResize: options.readOnlyAllowResize !== false });

  const sheetName = (options.sheetName != null ? options.sheetName : 'Issue Report').replace(/[\\/*?:\[\]]/g, '').slice(0, 31) || 'Issue Report';
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  XLSXStyle.writeFile(wb, fileName, { showGridlines: false });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatUtcDate(date) {
  if (date == null) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = MONTHS[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${h}:${m}`;
}

/**
 * Performed On display format: 16-Mar-2026 11:24 (local time).
 * Use in DCF Report, Visit Report, Action Item Report.
 */
export function formatPerformedOnDisplay(value) {
  if (value == null) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS[d.getMonth()] || '';
  const year = d.getFullYear();
  const hour = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hour}:${min}`;
}

/** Format UTC for DCF Report: "2026-03-13 11:26" (no seconds). */
export function formatUtcYmdHm(value) {
  if (value == null) return '';
  const str = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
  const parseAsUtc = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
  const d = new Date(parseAsUtc);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}-${mo}-${day} ${h}:${m}`;
}

/**
 * Format AuditDtl.dRecordedOnUTC for display. DB stores UTC; no conversion — show as-is.
 * If API sends datetime without 'Z', treat as UTC so we don't apply local timezone.
 */
function formatRecordedOnUtcDisplay(value) {
  if (value == null) return '';
  const str = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
  const parseAsUtc = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
  const d = new Date(parseAsUtc);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = MONTHS[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${h}:${m}`;
}

/** Same UTC parsing as formatRecordedOnUtcDisplay; date portion only (CRA Visit Report columns). */
function formatRecordedOnUtcDateOnly(value) {
  if (value == null) return '';
  const str = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
  const parseAsUtc = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
  const d = new Date(parseAsUtc);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = MONTHS[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  return `${day}-${month}-${year}`;
}

/** Parse AuditDtl-style UTC value to Date (same rules as formatRecordedOnUtcDisplay). */
function parseRecordedOnUtcDate(value) {
  if (value == null) return null;
  const str = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
  const parseAsUtc = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
  const d = new Date(parseAsUtc);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Calendar date (y, m, d) in the row's "Exported At Offset" zone: UTC instant shifted by offset, then take UTC date parts.
 * Matches formatPerformedOnUtcToLocal semantics for day boundaries.
 */
function localCalendarYmdInOffset(utcValue, offsetStr) {
  const d = parseRecordedOnUtcDate(utcValue);
  if (!d) return null;
  const offsetMinutes = parseOffsetToMinutes(offsetStr);
  const shifted = new Date(d.getTime() + offsetMinutes * 60 * 1000);
  return {
    y: shifted.getUTCFullYear(),
    m: shifted.getUTCMonth(),
    d: shifted.getUTCDate(),
  };
}

/** Calendar date parts from a general date value using local calendar semantics. */
function localCalendarYmdFromDateValue(value) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return {
    y: d.getFullYear(),
    m: d.getMonth(),
    d: d.getDate(),
  };
}

/**
 * Duration days for CRF Data Report from Actual End Date (Day 0) to event day.
 * Event day is computed in row offset; Actual End Date uses its own local date.
 */
function crfDurationDaysFromActualEndDate(row) {
  if (!row || row.dRecordedOnUTC == null) return '';
  const actualEndDate = row.actualEndDate ?? row.ActualEndDate;
  if (actualEndDate == null) return '';
  const eventOff = row.vRecordedAtOffSet ?? row.vRecordedAtOffset;
  const eventYmd = localCalendarYmdInOffset(row.dRecordedOnUTC, eventOff);
  const startYmd = localCalendarYmdFromDateValue(actualEndDate);
  if (!eventYmd || !startYmd) return '';
  const startUtcDay = Date.UTC(startYmd.y, startYmd.m, startYmd.d);
  const eventUtcDay = Date.UTC(eventYmd.y, eventYmd.m, eventYmd.d);
  const days = Math.round((eventUtcDay - startUtcDay) / 86400000);
  return String(Math.max(0, days));
}

/** Style for Form Name row: bold, larger font. */
const formNameStyle = {
  font: { bold: true, sz: 14 },
};

/** Style for header metadata rows (Exported by, Exported on UTC, Exported At Offset): bold. */
const headerMetaStyle = {
  font: { bold: true },
};

/** Style for header column row: bold. */
const headerRowStyle = {
  font: { bold: true },
};

/**
 * Adds Excel auto-filter dropdowns to a worksheet.
 * headerRow is 1-based: 1 for simple exports, 7 for metadata-block exports.
 */
function applyAutoFilter(ws, headerRow, columnCount, rowCount) {
  if (!ws || !columnCount || columnCount <= 0) return;
  const startRow = Math.max(1, Number(headerRow) || 1);
  const endRow = Math.max(startRow, Number(rowCount) || startRow);
  const lastCol = XLSX.utils.encode_col(columnCount - 1);
  ws['!autofilter'] = { ref: `A${startRow}:${lastCol}${endRow}` };
}

/**
 * Protect a worksheet so values cannot be modified while keeping filters usable.
 * In SheetJS protection flags, false means enabled and true means disabled.
 */
function applyReadOnlyProtection(ws, { allowResize = false } = {}) {
  if (!ws) return;
  ws['!protect'] = {
    selectLockedCells: false,
    selectUnlockedCells: false,
    formatCells: true,
    formatColumns: !allowResize ? true : false,
    formatRows: !allowResize ? true : false,
    insertColumns: true,
    insertRows: true,
    insertHyperlinks: true,
    deleteColumns: true,
    deleteRows: true,
    sort: false,
    autoFilter: false,
    pivotTables: true,
    objects: true,
    scenarios: true,
  };
}

function excelColumnLetters(count) {
  const letters = [];
  for (let i = 0; i < count; i++) letters.push(XLSXStyle.utils.encode_col(i));
  return letters;
}

/**
 * Export Audit Trail Report to Excel with header block and data columns (no Reason column).
 * Columns: Sr No., Project, Site, Visit Code/ID, Visit Type (when visit columns included), Field Name, Old Value, New Value, Activity Type, Exported By, Exported on UTC (AuditDtl.dRecordedOnUTC), Exported At Offset (AuditDtl.vRecordedAtOffSet).
 * Co-monitor appears as a row (Field Name = Co-Monitor, Old/New Value = names), not a separate column.
 * Header block: Form Name, Exported by (same format), Exported on UTC, Exported At Offset (e.g. +05:30).
 * @param {Array<Object>} dataRows - AuditDtlReportDto[] (vProjectCode, nSiteNo, vFieldName, vOldValue, vNewValue, vEvent, performedBy, dRecordedOnUTC, vRecordedAtOffSet, vRemark)
 * @param {string} fileName - Download filename (e.g. 'AuditTrailReport_2026-02-15.xlsx')
 * @param {Object} options - { formName?, exportedBy?, exportedOnUtc?, offset? }
 */
export function exportAuditReportToExcel(dataRows, fileName, options = {}) {
  const formName = options.formName != null ? options.formName : 'Audit Trail Report';
  const exportedBy = options.exportedBy != null ? options.exportedBy : '';
  const exportedOnUtc = options.exportedOnUtc != null ? options.exportedOnUtc : formatUtcDate(new Date());
  const offset = options.offset != null && options.offset !== '' ? options.offset : '+05:30';
  const offsetDisplay = (v) => (formatOffsetForDisplay(v) === '—' ? '' : formatOffsetForDisplay(v));
  const excludeVisitColumns = options.excludeVisitColumns === true;

  const headerRow = excludeVisitColumns
    ? ['Sr No.', 'Project', 'Site', 'Field Name', 'Old Value', 'New Value', 'Activity Type', 'Performed By', 'Performed on UTC', 'Performed At Offset']
    : ['Sr No.', 'Project', 'Site', 'Visit ID', 'Visit Type', 'Field Name', 'Old Value', 'New Value', 'Activity Type', 'Performed By', 'Performed on UTC', 'Performed At Offset'];
  const colCount = headerRow.length;

  const rows = [
    [`Form Name:- ${formName}`],
    [],
    [`Exported by: ${exportedBy}`],
    [`Exported on UTC: ${exportedOnUtc}`],
    [`Exported At Offset: ${offsetDisplay(offset)}`],
    [],
    headerRow,
  ];

  const padRow = (arr) => (arr.length >= colCount ? arr : [...arr, ...Array(colCount - arr.length).fill('')]);
  rows[0] = padRow(rows[0]);
  rows[1] = padRow(rows[1] || []);
  rows[2] = padRow(rows[2]);
  rows[3] = padRow(rows[3]);
  rows[4] = padRow(rows[4]);

  const cell = (v) => (v != null && v !== '' ? String(v) : '');
  dataRows.forEach((r, idx) => {
    const performedOnUtc = r.dRecordedOnUTC != null ? formatRecordedOnUtcDisplay(r.dRecordedOnUTC) : '';
    const performedAtOffset = (r.vRecordedAtOffSet ?? r.vRecordedAtOffset) != null ? formatOffsetForDisplay(r.vRecordedAtOffSet ?? r.vRecordedAtOffset) : '';
    if (excludeVisitColumns) {
      rows.push([
        idx + 1,
        cell(r.vProjectCode),
        cell(r.vSiteDisplay ?? r.nSiteNo),
        cell(getAuditFieldDisplayName(r.vFieldName, r.vTableName)),
        cell(r.vOldValue),
        cell(r.vNewValue),
        cell(r.vEvent),
        cell(r.performedBy || r.vRecordedSign),
        cell(performedOnUtc),
        cell(performedAtOffset === '—' ? '' : performedAtOffset),
      ]);
    } else {
      rows.push([
        idx + 1,
        cell(r.vProjectCode),
        cell(r.vSiteDisplay ?? r.nSiteNo),
        cell(r.vSiteCode),
        cell(r.vVisitType),
        cell(getAuditFieldDisplayName(r.vFieldName, r.vTableName)),
        cell(r.vOldValue),
        cell(r.vNewValue),
        cell(r.vEvent),
        cell(r.performedBy || r.vRecordedSign),
        cell(performedOnUtc),
        cell(performedAtOffset === '—' ? '' : performedAtOffset),
      ]);
    }
  });

  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const wb = XLSXStyle.utils.book_new();

  if (ws['A1']) {
    ws['A1'].s = formNameStyle;
  }
  if (ws['A3']) ws['A3'].s = headerMetaStyle;
  if (ws['A4']) ws['A4'].s = headerMetaStyle;
  if (ws['A5']) ws['A5'].s = headerMetaStyle;

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: colCount - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: colCount - 1 } },
  ];

  const headerCols = excelColumnLetters(colCount);
  headerCols.forEach((col) => {
    const ref = `${col}7`;
    if (ws[ref]) {
      ws[ref].s = headerRowStyle;
    }
  });

  ws['!cols'] = excludeVisitColumns
    ? [
      { wch: 7 },   // Sr No.
      { wch: 18 },  // Project
      { wch: 28 },  // Site
      { wch: 28 },  // Field Name
      { wch: 32 },  // Old Value
      { wch: 32 },  // New Value
      { wch: 14 },  // Activity Type
      { wch: 36 },  // Exported By
      { wch: 20 },  // Exported on UTC
      { wch: 18 },  // Exported At Offset
    ]
    : [
      { wch: 7 },   // A: Sr No.
      { wch: 18 },  // B: Project
      { wch: 28 },  // C: Site
      { wch: 14 },  // D: Visit Code
      { wch: 22 },  // E: Visit Type
      { wch: 28 },  // F: Field Name
      { wch: 32 },  // G: Old Value
      { wch: 32 },  // H: New Value
      { wch: 14 },  // I: Activity Type
      { wch: 36 },  // J: Exported By
      { wch: 20 },  // K: Exported on UTC
      { wch: 18 },  // L: Exported At Offset
    ];

  applyAutoFilter(ws, 7, colCount, rows.length);
  applyReadOnlyProtection(ws, { allowResize: true });

  const safeSheetName = (formName || 'Audit Trail Report').replace(/[\\/*?:\[\]]/g, '').slice(0, 31) || 'Audit Trail Report';
  XLSXStyle.utils.book_append_sheet(wb, ws, safeSheetName);
  XLSXStyle.writeFile(wb, fileName, { showGridlines: false });
}

/**
 * Export Visit Report to Excel with header block and 11 columns.
 * Columns: Sr No., Project, Site, Visit ID, Visit Type, Report Name, Status, Difference(Days), Performed By, Performed On UTC, Performed At Offset (from AuditDtl).
 * Difference(Days): calendar days from Actual End Date (Day 0) to each row event day.
 * @param {Array<Object>} dataRows - CRFDataReportRowDto[] (project, siteNo, visitId, visitType, reportName, status, performedBy, dRecordedOnUTC, vRecordedAtOffSet)
 * @param {string} fileName - Download filename (e.g. 'CRFDataReport_2026-03-13.xlsx')
 * @param {Object} options - { formName?, exportedBy?, exportedOnUtc?, offset? }
 */
export function exportCRFDataReportToExcel(dataRows, fileName, options = {}) {
  const formName = options.formName != null ? options.formName : 'Visit Report';
  const exportedBy = options.exportedBy != null ? options.exportedBy : '';
  const exportedOnUtc = options.exportedOnUtc != null ? options.exportedOnUtc : formatUtcDate(new Date());
  const offset = options.offset != null && options.offset !== '' ? options.offset : '+05:30';
  const offsetDisplay = (v) => (formatOffsetForDisplay(v) === '—' ? '' : formatOffsetForDisplay(v));

  const headerRow = [
    'Sr No.',
    'Project',
    'Site',
    'Visit ID',
    'Visit Type',
    'Report Name',
    'Status',
    'Difference(Days)',
    'Performed By',
    'Performed On UTC',
    'Performed At Offset',
  ];
  const colCount = headerRow.length;

  const rows = [
    [`Form Name:- ${formName}`],
    [],
    [`Exported by: ${exportedBy}`],
    [`Exported on UTC: ${exportedOnUtc}`],
    [`Exported At Offset: ${offsetDisplay(offset)}`],
    [],
    headerRow,
  ];

  const padRow = (arr) => (arr.length >= colCount ? arr : [...arr, ...Array(colCount - arr.length).fill('')]);
  rows[0] = padRow(rows[0]);
  rows[1] = padRow(rows[1] || []);
  rows[2] = padRow(rows[2]);
  rows[3] = padRow(rows[3]);
  rows[4] = padRow(rows[4]);
  rows[5] = padRow(rows[5] || []);

  const cell = (v) => (v != null && v !== '' ? String(v) : '');
  dataRows.forEach((r, idx) => {
    const performedOnUtc = r.dRecordedOnUTC != null ? formatRecordedOnUtcDisplay(r.dRecordedOnUTC) : '';
    const performedAtOffset = (r.vRecordedAtOffSet ?? r.vRecordedAtOffset) != null ? formatOffsetForDisplay(r.vRecordedAtOffSet ?? r.vRecordedAtOffset) : '';
    const diffDays = crfDurationDaysFromActualEndDate(r);
    rows.push([
      cell(idx + 1),
      cell(r.project),
      cell(r.siteNo),
      cell(r.visitId ?? r.VisitId),
      cell(r.visitType),
      cell(r.reportName),
      cell(r.status),
      cell(diffDays),
      cell(r.performedBy ?? r.PerformedBy),
      cell(performedOnUtc),
      cell(performedAtOffset === '—' ? '' : performedAtOffset),
    ]);
  });

  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const wb = XLSXStyle.utils.book_new();

  if (ws['A1']) ws['A1'].s = formNameStyle;
  if (ws['A3']) ws['A3'].s = headerMetaStyle;
  if (ws['A4']) ws['A4'].s = headerMetaStyle;
  if (ws['A5']) ws['A5'].s = headerMetaStyle;

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: colCount - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: colCount - 1 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: colCount - 1 } },
  ];

  Array.from({ length: colCount }, (_, c) => XLSXStyle.utils.encode_col(c)).forEach((col) => {
    const ref = `${col}7`;
    if (ws[ref]) ws[ref].s = headerRowStyle;
  });

  ws['!cols'] = [
    { wch: 7 },
    { wch: 18 },
    { wch: 24 },
    { wch: 14 },
    { wch: 22 },
    { wch: 36 },
    { wch: 12 },
    { wch: 16 },
    { wch: 36 },
    { wch: 20 },
    { wch: 18 },
  ];

  applyAutoFilter(ws, 7, colCount, rows.length);
  applyReadOnlyProtection(ws, { allowResize: true });

  const safeSheetName = (formName || 'Visit Report').replace(/[\\/*?:\[\]]/g, '').slice(0, 31) || 'Visit Report';
  XLSXStyle.utils.book_append_sheet(wb, ws, safeSheetName);
  XLSXStyle.writeFile(wb, fileName, { showGridlines: false });
}

/**
 * CRA Visit Report: one row per visit with lifecycle columns (matches API CraVisitReportRowDto camelCase).
 * @param {Array<Object>} dataRows
 * @param {string} fileName
 * @param {{ formName?: string, exportedBy?: string, exportedOnUtc?: string, offset?: string }} options
 */
export function exportCraVisitReportToExcel(dataRows, fileName, options = {}) {
  const formName = options.formName != null ? options.formName : 'CRA Visit Report';
  const exportedBy = options.exportedBy != null ? options.exportedBy : '';
  const exportedOnUtc = options.exportedOnUtc != null ? options.exportedOnUtc : formatUtcDate(new Date());
  const offset = options.offset != null && options.offset !== '' ? options.offset : '+05:30';
  const offsetDisplay = (v) => (formatOffsetForDisplay(v) === '—' ? '' : formatOffsetForDisplay(v));
  const ordinalWord = (n) => {
    const words = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth'];
    return words[n - 1] || `${n}th`;
  };
  const getReviewerApprovals = (r) => {
    const list = r?.reviewerApprovals ?? r?.ReviewerApprovals;
    return Array.isArray(list) ? list : [];
  };
  const maxConfiguredReviewers = Math.max(
    0,
    ...((Array.isArray(dataRows) ? dataRows : []).map((r) => {
      const explicitCount = Number(r?.reviewerCount ?? r?.ReviewerCount ?? 0);
      const approvalsCount = getReviewerApprovals(r).length;
      return Math.max(explicitCount, approvalsCount);
    }))
  );
  const reviewerCount = Math.max(
    maxConfiguredReviewers,
    (Array.isArray(dataRows) ? dataRows : []).some((r) => (r?.secondReviewBy ?? r?.SecondReviewBy ?? '') !== '' || (r?.secondReviewApprovalDate ?? r?.SecondReviewApprovalDate) != null)
      ? 2
      : (Array.isArray(dataRows) ? dataRows : []).some((r) => (r?.firstReviewBy ?? r?.FirstReviewBy ?? '') !== '' || (r?.firstReviewApprovalDate ?? r?.FirstReviewApprovalDate) != null)
        ? 1
        : 0
  );

  const headerRow = [
    'Project',
    'SiteID',
    'Site Name',
    'City',
    'Country',
    'Name of Investigator',
    'Type Of Visit',
    'Visit ID',
    'Schedule Start Date',
    'Schedule End Date',
    'Generate visit letter Status',
    'Confirmation Sent On',
    'Actual Start Date',
    'Actual End Date',
    'Visit Days',
    'Visit Status',
    'Report Status',
    'Report Status Duration',
    'Submission Date',
    'Submitted By',
    'Follow-up Letter Status',
    'PI acknowledgement',
    'Unlocked By',
    'Unlocked Date',
  ];
  for (let i = 1; i <= reviewerCount; i += 1) {
    const label = ordinalWord(i);
    headerRow.splice(20 + (i - 1) * 2, 0, `${label} Reviewed by`, `${label} Reviewer Approval Date`);
  }
  const colCount = headerRow.length;

  const rows = [
    [`Form Name:- ${formName}`],
    [],
    [`Exported by: ${exportedBy}`],
    [`Exported on UTC: ${exportedOnUtc}`],
    [`Exported At Offset: ${offsetDisplay(offset)}`],
    [],
    headerRow,
  ];

  const padRow = (arr) => {
    const src = Array.isArray(arr) ? arr : [];
    const out = src.slice(0, colCount);
    while (out.length < colCount) out.push('');
    return out;
  };
  rows[0] = padRow(rows[0]);
  rows[1] = padRow(rows[1] || []);
  rows[2] = padRow(rows[2]);
  rows[3] = padRow(rows[3]);
  rows[4] = padRow(rows[4]);
  rows[5] = padRow(rows[5] || []);

  const cell = (v) => (v != null && v !== '' ? String(v) : '');
  const cellDateOnly = (v) => (v != null && v !== '' ? formatRecordedOnUtcDateOnly(v) : '');

  dataRows.forEach((r) => {
    const reviewerApprovals = getReviewerApprovals(r);
    const reviewerCells = [];
    for (let i = 0; i < reviewerCount; i += 1) {
      const approval = reviewerApprovals[i];
      const reviewerBy = approval?.reviewerBy ?? approval?.ReviewerBy;
      const reviewerApprovalDate = approval?.reviewerApprovalDate ?? approval?.ReviewerApprovalDate;
      if (approval) {
        reviewerCells.push(cell(reviewerBy), cellDateOnly(reviewerApprovalDate));
      } else if (i === 0) {
        reviewerCells.push(cell(r.firstReviewBy ?? r.FirstReviewBy), cellDateOnly(r.firstReviewApprovalDate ?? r.FirstReviewApprovalDate));
      } else if (i === 1) {
        reviewerCells.push(cell(r.secondReviewBy ?? r.SecondReviewBy), cellDateOnly(r.secondReviewApprovalDate ?? r.SecondReviewApprovalDate));
      } else {
        reviewerCells.push('', '');
      }
    }
    const piAckDisplay = r.piAcknowledgementDisplay ?? r.PiAcknowledgementDisplay;
    const piAckValue = piAckDisplay
      ? cell(piAckDisplay)
      : cellDateOnly(r.piAcknowledgementDate ?? r.PiAcknowledgementDate);
    rows.push(
      padRow([
        cell(r.project ?? r.Project),
        cell(r.siteID ?? r.SiteID),
        cell(r.siteName ?? r.SiteName),
        cell(r.city ?? r.City),
        cell(r.country ?? r.Country),
        cell(r.nameOfInvestigator ?? r.NameOfInvestigator),
        cell(r.typeOfVisit ?? r.TypeOfVisit),
        cell(r.refNo ?? r.RefNo),
        cellDateOnly(r.scheduleStartDate ?? r.ScheduleStartDate),
        cellDateOnly(r.scheduleEndDate ?? r.ScheduleEndDate),
        cell(r.generateVisitLetterStatus ?? r.GenerateVisitLetterStatus),
        cellDateOnly(r.confirmPIAvailabilityDate ?? r.ConfirmPIAvailabilityDate),
        cellDateOnly(r.actualStartDate ?? r.ActualStartDate),
        cellDateOnly(r.actualEndDate ?? r.ActualEndDate),
        cell(r.visitDays ?? r.VisitDays),
        cell(r.visitStatus ?? r.VisitStatus),
        cell(r.reportStatus ?? r.ReportStatus),
        cell(r.reportStatusDurationDays ?? r.ReportStatusDurationDays),
        cellDateOnly(r.submissionDate ?? r.SubmissionDate),
        cell(r.submittedBy ?? r.SubmittedBy),
        ...reviewerCells,
        cell(r.followUpLetterStatus ?? r.FollowUpLetterStatus),
        piAckValue,
        cell(r.unlockedBy ?? r.UnlockedBy),
        cellDateOnly(r.unlockedOnUtc ?? r.UnlockedOnUtc),
      ])
    );
  });

  const ws = XLSXStyle.utils.aoa_to_sheet(rows);
  const lastColLetter = XLSXStyle.utils.encode_col(colCount - 1);
  ws['!ref'] = `A1:${lastColLetter}${rows.length}`;
  const wb = XLSXStyle.utils.book_new();

  if (ws['A1']) ws['A1'].s = formNameStyle;
  if (ws['A3']) ws['A3'].s = headerMetaStyle;
  if (ws['A4']) ws['A4'].s = headerMetaStyle;
  if (ws['A5']) ws['A5'].s = headerMetaStyle;

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: colCount - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: colCount - 1 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: colCount - 1 } },
  ];

  Array.from({ length: colCount }, (_, c) => XLSXStyle.utils.encode_col(c)).forEach((col) => {
    const ref = `${col}7`;
    if (ws[ref]) ws[ref].s = headerRowStyle;
  });

  ws['!cols'] = headerRow.map(() => ({ wch: 18 }));
  ws['!cols'][0] = { wch: 14 };
  ws['!cols'][7] = { wch: 14 };
  ws['!cols'][colCount - 1] = { wch: 22 };

  applyAutoFilter(ws, 7, colCount, rows.length);
  applyReadOnlyProtection(ws, { allowResize: true });

  const safeSheetName = (formName || 'CRA Visit Report').replace(/[\\/*?:\[\]]/g, '').slice(0, 31) || 'CRA Visit Report';
  XLSXStyle.utils.book_append_sheet(wb, ws, safeSheetName);
  XLSXStyle.writeFile(wb, fileName, { showGridlines: false });
}
